import {
  deleteResume,
  getProfessionalResume,
  getSignupRequestId,
  getTechnicianDiploma,
  pharmacistActions,
  pharmacistResume,
  professionalDeleteDiploma,
  professionalDeleteSignupDiploma,
  professionalDeleteSignupResume,
  professionalUploadDiploma,
  professionalUploadSignupDiploma,
  professionalUploadSignupResume,
  proInfo,
  setProfessionalResume,
  setTechnicianDiploma,
  successSelector,
  uploadResume,
} from '@pharmaplan/common';

import { stub } from '@pharmaplan/common/helpers/utils';

import { FormikValues } from 'formik';
import { useAppSelector } from '../useAppSelector';
import { useAppDispatch } from '../useAppDispatch';
import strings from '../../localization';

export enum fileUploadTypes {
  resume = 'resume',
  diploma = 'diploma',
  signupDiploma = 'signupDiploma',
  adminDiploma = 'adminDiploma',
  signupResume = 'signupResume',
}

const { diploma, signupDiploma, signupResume, resume } = fileUploadTypes;

const {
  uploadDiplomaCertificate: uploadDiplomaAction,
  uploadResume: uploadResumeAction,
} = pharmacistActions;

interface IConfig {
  fileName?: string,
  fileUri?: string,
  label: string,
  customOpenDocument?: () => void;
  uploadSuccess: boolean,
  onSuccess: () => void;
  onDelete: () => void;
  onUpload: (values: FormikValues) => void;
}

const mimeMap = {
  png: 'image/png',
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  docx: 'application/octet-stream',
  doc: 'application/octet-stream',
};

const determineMime = (fileName:string) => {
  const re = /(?:\.([^.]+))?$/;

  const extension = re?.exec?.(fileName)?.[1];

  return mimeMap[extension as keyof typeof mimeMap];
};

const useFileUpload = (type: fileUploadTypes) => {
  const dispatch = useAppDispatch();

  const professionalInfo = useAppSelector(proInfo);
  const technicianDiploma = useAppSelector(getTechnicianDiploma);
  const professionalResume = useAppSelector(getProfessionalResume);

  const uploadResumeSuccess = useAppSelector((state) =>
    successSelector([uploadResumeAction], state));

  // THIS IS OLD IMPLEMENTATION WHICH WE NEED TO MAINTAIN
  const resumeObj = useAppSelector(pharmacistResume);

  const signupRequestId = useAppSelector(getSignupRequestId);

  const uploadDiplomaSuccess = useAppSelector((state) =>
    successSelector([uploadDiplomaAction], state));

  const config: { [key: string]: IConfig } = {
    [diploma]: {
      fileName: professionalInfo?.certificateFileName,
      fileUri: professionalInfo?.certificateUrl,
      label: strings.uploadYourDiploma,
      uploadSuccess: uploadDiplomaSuccess,
      onSuccess: stub,
      onDelete: () => {
        dispatch(professionalDeleteDiploma());
      },
      onUpload: (values: FormikValues) => {
        const { file } = values ?? {};

        const form = new FormData();
        form.append('file', file);
        dispatch(professionalUploadDiploma(form));
      },
    },
    [signupDiploma]: {
      fileName: technicianDiploma?.name,
      fileUri: technicianDiploma?.uri,
      label: strings.uploadYourDiploma,
      uploadSuccess: false,
      onSuccess: stub,
      onDelete: () => {
        dispatch(professionalDeleteSignupDiploma(signupRequestId ?? ''));
        dispatch(setTechnicianDiploma({ name: '', uri: '' }));
      },
      onUpload: (values: FormikValues) => {
        const { file } = values ?? {};

        const form = new FormData();
        form.append('file', file);

        const uri = URL.createObjectURL(file);
        dispatch(setTechnicianDiploma({ name: file.name, uri }));
        dispatch(
          professionalUploadSignupDiploma({
            form,
            signupRequestId: signupRequestId ?? '',
          }),
        );
      },
    },
    [signupResume]: {
      fileName: professionalResume?.name,
      fileUri: professionalResume?.uri,
      label: strings.uploadYourResume,
      uploadSuccess: false,
      onSuccess: stub,
      onDelete: () => {
        dispatch(professionalDeleteSignupResume(signupRequestId ?? ''));
        dispatch(setProfessionalResume({ name: '', uri: '' }));
      },
      onUpload: (values: FormikValues) => {
        const { file } = values ?? {};

        const form = new FormData();
        form.append('file', file);

        const uri = URL.createObjectURL(file);
        dispatch(setProfessionalResume({ name: file.name, uri }));

        dispatch(
          professionalUploadSignupResume({
            form,
            signupRequestId: signupRequestId ?? '',
          }),
        );
      },
    },
    [resume]: {
      fileName: resumeObj?.fileName,
      fileUri: resumeObj?.resume,
      customOpenDocument: () => {
        const byteCharacters = atob(resumeObj.resume);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i += 1) {
          byteNumbers[i] = byteCharacters.codePointAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const file = new Blob([byteArray], { type: `${determineMime(resumeObj.fileName)};base64` });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL);
      },
      label: strings.uploadYourResume,
      uploadSuccess: uploadResumeSuccess,
      onSuccess: stub,
      onDelete: () => {
        dispatch(deleteResume());
      },
      onUpload: (values: FormikValues) => {
        (async () => {
          const file = values?.file;
          const fileName = file.name;
          const arrayBuffer = await file.arrayBuffer();

          dispatch(uploadResume({
            document: new Uint8Array(arrayBuffer) as unknown as Buffer,
            path: fileName,
          }));
        })();
      },
    },
  };

  return config[type as keyof typeof config];
};

export default useFileUpload;
